import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { format } from 'date-fns';

import { Button, IconButton, LABEL_TYPE, Spinner } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { IconButtonVariant } from '@/components/generic/IconButton/icon-button-config';
import { Label } from '@/components/generic/Label/Label';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { dateAndHoursFormatRequest, dateFormatRequest } from '@/constants';
import { DeviceScheduleContentType } from '@/enums';
import { isTimeSlotActive } from '@/helpers/DeviceScheduleHelper';
import { showToast, ToastType } from '@/helpers/toast';
import { EditPenIcon, TrashIcon } from '@/resources';
import { useDeleteDeviceScheduleMutation, useGetDeviceScheduleByIdQuery } from '@/services';
import {
  useRepeatDeviceScheduleMutation,
  useUpdateDeviceScheduleMutation
} from '@/services/deviceSchedule.service';
import { TestIds } from '@/shared/test-ids';
import { UpdateTimeSlotPayload } from '@/types';

import RepeatSlotModal from './RepeatSlotModal';
import { DefaultTab, PlaylistTab, PushTab } from './Tab';
import { TimeSlotModal } from './TimeSlotModal';
import { DeviceScheduleInterruptionModal } from './modals/DeviceScheduleInterruptionModal';

enum CONTENT_SETUP_TABS {
  DEFAULT = 'default',
  PLAYLIST = 'playlist',
  PUSH = 'push'
}
const CONTENT_SETUP_TABS_CONFIG = [
  {
    value: CONTENT_SETUP_TABS.DEFAULT,
    translateKey: 'default',
    isDisabled: false
  },
  {
    value: CONTENT_SETUP_TABS.PLAYLIST,
    translateKey: 'playlist',
    isDisabled: false
  },
  {
    value: CONTENT_SETUP_TABS.PUSH,
    translateKey: 'push',
    isDisabled: false
  }
];

type EventSetupProps = {
  deviceScheduleId: number;
  onEventSelect?: (eventId: string | null) => void;
  refetchDeviceSchedules?: () => void;
};

export const ContentSetup = ({
  deviceScheduleId,
  onEventSelect,
  refetchDeviceSchedules
}: EventSetupProps) => {
  const [t] = useTranslation('common');
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isRepeatModalOpen, setIsRepeatModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<CONTENT_SETUP_TABS | null>(null);
  const [isSlotInterruptionModalOpen, setIsSlotInterruptionModalOpen] = useState(false);
  const [isExecutingSlotInterruption, setIsExecutingSlotInterruption] = useState(false);
  const pendingSlotInterruptionAction = useRef<(() => void | Promise<void>) | null>(null);

  const { data: deviceSchedule, refetch: refetchDeviceScheduleById } =
    useGetDeviceScheduleByIdQuery(deviceScheduleId);

  const { mutate: updateDeviceSchedule } = useUpdateDeviceScheduleMutation(queryClient, {
    onSuccess: () => {
      setIsEditModalOpen(false);
      refetchDeviceScheduleById();
      if (refetchDeviceSchedules) {
        refetchDeviceSchedules();
      }
    },
    onError: () => {
      setIsEditModalOpen(false);
    }
  });
  const { mutate: deleteDeviceSchedule } = useDeleteDeviceScheduleMutation(queryClient, {
    onSuccess: () => {
      setIsOpenDeleteModal(false);
      if (refetchDeviceSchedules) {
        refetchDeviceSchedules();
      }
    },
    onError: () => {
      setIsOpenDeleteModal(false);
    }
  });
  const { mutate: repeatDeviceSchedule } = useRepeatDeviceScheduleMutation(queryClient, {
    onSuccess: () => {
      setIsRepeatModalOpen(false);
      refetchDeviceScheduleById();
      if (refetchDeviceSchedules) {
        refetchDeviceSchedules();
      }
      showToast(ToastType.SUCCESS, t('deviceSchedulesDuplicatedSuccessfully'));
    },
    onError: () => {
      setIsRepeatModalOpen(false);
      showToast(ToastType.ERROR, t('somethingWentWrong'));
    }
  });

  const initialContentTab = useMemo(() => {
    const contentType = deviceSchedule?.data?.contentType;
    if (!contentType) {
      return null;
    }

    if (
      contentType === DeviceScheduleContentType.NewAsset ||
      contentType === DeviceScheduleContentType.EventAsset ||
      contentType === DeviceScheduleContentType.OrganizationAsset
    ) {
      return CONTENT_SETUP_TABS.PUSH;
    } else if (contentType === DeviceScheduleContentType.Playlist) {
      return CONTENT_SETUP_TABS.PLAYLIST;
    } else if (contentType === DeviceScheduleContentType.Default) {
      return CONTENT_SETUP_TABS.DEFAULT;
    }
    return null;
  }, [deviceSchedule?.data?.contentType]);

  const isLoading = !initialContentTab;

  useEffect(() => {
    if (!initialContentTab) {
      return;
    }

    setActiveTab(initialContentTab);
  }, [initialContentTab]);

  const formatDeviceScheduleDate = (
    allDay: boolean,
    start?: string | Date,
    end?: string | Date
  ) => {
    if (!start || !end) {
      return '';
    }

    const startDate = new Date(start);
    const endDate = new Date(end);

    const options: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };
    const dateStr = startDate.toLocaleDateString('en-US', options);

    if (allDay) {
      return `${dateStr} | ${t('allDay')}`;
    }

    const formatTime = (d: Date) => {
      let hours = d.getHours();
      const minutes = d.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      return `${hours} : ${minutes} ${ampm}`;
    };

    return `${dateStr} | ${formatTime(startDate)} - ${formatTime(endDate)}`;
  };

  const scheduleStr = deviceSchedule
    ? formatDeviceScheduleDate(
        deviceSchedule.data.allDay,
        deviceSchedule.data.start,
        deviceSchedule.data.end
      )
    : '';

  const handleCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
  };

  const requestSlotInterruptionWarning = (action: () => void | Promise<void>) => {
    const isSlotActive = isTimeSlotActive(deviceSchedule?.data?.start, deviceSchedule?.data?.end);

    if (!isSlotActive || deviceSchedule?.data?.contentType === DeviceScheduleContentType.Default) {
      return false;
    }

    pendingSlotInterruptionAction.current = action;
    setIsSlotInterruptionModalOpen(true);
    return true;
  };

  const handleConfirmSlotInterruptionWarning = async () => {
    if (!pendingSlotInterruptionAction.current) {
      return;
    }

    setIsExecutingSlotInterruption(true);

    try {
      await pendingSlotInterruptionAction.current();
    } finally {
      setIsExecutingSlotInterruption(false);
      setIsSlotInterruptionModalOpen(false);
      pendingSlotInterruptionAction.current = null;
    }
  };

  const updateTimeSlotValues = (values: UpdateTimeSlotPayload) => {
    if (!deviceSchedule?.data) {
      throw new Error('Device schedule data is not available');
    }
    const start = new Date(values.date ?? new Date());
    const end = new Date(values.date ?? new Date());

    if (values.allDay) {
      start.setHours(0, 0, 1, 0);
      end.setHours(23, 59, 59, 0);
    } else {
      const [startH, startM] = values.startTime.split(':').map(Number);
      start.setHours(startH, startM, 0, 0);

      const [endH, endM] = values.endTime.split(':').map(Number);
      end.setHours(endH, endM, 0, 0);
    }

    updateDeviceSchedule({
      id: deviceSchedule.data.id,
      screenId: deviceSchedule.data.screenId,
      start: format(start, dateAndHoursFormatRequest),
      end: format(end, dateAndHoursFormatRequest),
      allDay: values.allDay
    });
  };

  const handleRepeatUntil = (endDate: Date) => {
    if (!deviceScheduleId) {
      return;
    }

    repeatDeviceSchedule({
      deviceScheduleId,
      repeatUntilDate: format(endDate, dateFormatRequest)
    });
  };

  const getTabTestId = (tab: CONTENT_SETUP_TABS) => {
    if (tab === CONTENT_SETUP_TABS.DEFAULT) {
      return TestIds.digitalSignage.contentSetupTabDefault;
    }

    if (tab === CONTENT_SETUP_TABS.PLAYLIST) {
      return TestIds.digitalSignage.contentSetupTabPlaylist;
    }

    return TestIds.digitalSignage.contentSetupTabPush;
  };

  return (
    <div
      data-testid={TestIds.digitalSignage.contentSetupPanel}
      className='relative flex size-full flex-col'>
      <div className='px-4 pt-4'>
        <div className='sticky right-0 top-0 z-20 flex w-full flex-col justify-between'>
          <div className='flex w-full justify-between'>
            <Label text={t('contentSetup')} type={LABEL_TYPE.H1} className='left-0 my-3 md:mt-0' />
            <div className='flex flex-row items-center gap-4'>
              <Button
                dataTestId={TestIds.digitalSignage.contentSetupRepeatUntilButton}
                variant={ButtonVariant.SECONDARY}
                handleOnClick={() => setIsRepeatModalOpen(true)}>
                {t('repeatUntil')}
              </Button>
              <IconButton
                dataTestId={TestIds.digitalSignage.contentSetupEditButton}
                variant={IconButtonVariant.PRIMARY}
                handleOnClick={() => setIsEditModalOpen(true)}>
                <EditPenIcon className='h-4 w-3 text-primary-blue md:h-5 md:w-4' />
              </IconButton>
              <IconButton
                dataTestId={TestIds.digitalSignage.contentSetupDeleteButton}
                variant={IconButtonVariant.PRIMARY}
                handleOnClick={() => setIsOpenDeleteModal(true)}>
                <TrashIcon className='h-4 w-3 md:h-5 md:w-4' />
              </IconButton>
            </div>
          </div>
          <Label text={scheduleStr} type={LABEL_TYPE.PARAGRAPH2} className='!text-secondary-gray' />
        </div>
        {!isLoading && (
          <div className='mt-5 flex w-full flex-col gap-y-2'>
            <Label
              text={t('selectWhatToPresentOnDevice')}
              type={LABEL_TYPE.PARAGRAPH2}
              className='!text-secondary-gray'
            />
            <div
              data-testid={TestIds.digitalSignage.contentSetupTabs}
              className='flex justify-between gap-x-1 rounded-[10px] border border-secondary-blue-gray p-1'>
              {CONTENT_SETUP_TABS_CONFIG.map((option) => (
                <Button
                  dataTestId={getTabTestId(option.value)}
                  variant={
                    activeTab === option.value ? ButtonVariant.PRIMARY : ButtonVariant.TERTIARY
                  }
                  key={option.translateKey}
                  disabled={option.isDisabled}
                  handleOnClick={() => setActiveTab(option.value)}
                  className='z-10 h-8 w-1/2'>
                  {t(option.translateKey)}
                </Button>
              ))}
            </div>
          </div>
        )}
        {isEditModalOpen && deviceSchedule?.data && (
          <TimeSlotModal
            date={new Date(deviceSchedule.data.start)}
            deviceSchedule={deviceSchedule.data}
            onClose={() => setIsEditModalOpen(false)}
            onEdit={updateTimeSlotValues}
          />
        )}
        {isRepeatModalOpen && deviceSchedule?.data && (
          <RepeatSlotModal
            startDate={new Date(deviceSchedule.data.start)}
            onClose={() => setIsRepeatModalOpen(false)}
            onAdd={handleRepeatUntil}
          />
        )}
      </div>
      {isLoading && <Spinner />}
      {!isLoading && deviceSchedule?.data && (
        <div className='flex-1 overflow-y-auto p-4'>
          {activeTab === CONTENT_SETUP_TABS.PLAYLIST && (
            <PlaylistTab
              deviceSchedule={deviceSchedule.data}
              requestSlotInterruptionWarning={requestSlotInterruptionWarning}
              refetchDeviceSchedules={refetchDeviceSchedules}
            />
          )}
          {activeTab === CONTENT_SETUP_TABS.PUSH && (
            <PushTab
              deviceScheduleId={deviceScheduleId}
              requestSlotInterruptionWarning={requestSlotInterruptionWarning}
              refetchDeviceSchedules={refetchDeviceSchedules}
            />
          )}
          {activeTab === CONTENT_SETUP_TABS.DEFAULT && (
            <DefaultTab
              deviceSchedule={deviceSchedule.data}
              requestSlotInterruptionWarning={requestSlotInterruptionWarning}
              refetchDeviceSchedules={refetchDeviceSchedules}
            />
          )}
        </div>
      )}
      {isOpenDeleteModal && (
        <ConfirmationModal
          isOpen={isOpenDeleteModal}
          message={t('deleteMessage', { item: scheduleStr })}
          onClose={handleCloseDeleteModal}
          onConfirm={() => {
            deleteDeviceSchedule(deviceSchedule?.data.id as number);
            if (onEventSelect) {
              onEventSelect(null);
            }
          }}
        />
      )}
      {isSlotInterruptionModalOpen && (
        <DeviceScheduleInterruptionModal
          isOpen={isSlotInterruptionModalOpen}
          onClose={() => {
            setIsSlotInterruptionModalOpen(false);
            pendingSlotInterruptionAction.current = null;
          }}
          onConfirm={handleConfirmSlotInterruptionWarning}
          message={t('slotInterruptionWarning')}
          isLoading={isExecutingSlotInterruption}
        />
      )}
    </div>
  );
};
